import { Box, Card, Grid, Stack } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { useParams } from 'react-router';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import ProviderWindow from 'src/careflow/common/ProviderWindow';
import { getEOCLastByCode, getFhirIdFromEntity } from 'src/utils/fhir';
import { PatientWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import {
  useBinary,
  useEpisodeOfCares,
  usePatient,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import StandardDialogProvider from 'src/careflow/ph-core/ui/base/StandardDialogProvider';
import { PatientLayout } from './components/PatientLayout';
import { RejectionFiles, UploadAvatar } from 'src/components/upload';
import { PatientBasicInfo } from '../patient-banner/PatientBasicInfo';
import PatientTabs from '../tabs/PatientTabs';
import IcomxResourceLauncher from '../../../../sections/crs/patient/components/icomx/IcomxResourceLauncher';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';

type FileRejectionByPatient = {
  [key: string]: FileRejection[];
};

export default function PatientDetails() {
  const [fileimg, setfileImg] = useState('');
  const [fileRejectionsByPatient, setFileRejectionsByPatient] =
    useState<FileRejectionByPatient | null>({});
  const { patientId } = useParams();
  const [patient, { isSuccess, refresh: refreshPatient }] = usePatient(patientId, {
    map: PatientWrapper,
  });
  const [binary] = useBinary(getFhirIdFromEntity(patient?.photo?.[0].url || ''));
  const [, { find: findEpisodeOfCare }] = useEpisodeOfCares({ autofetch: false });
  const { i18n } = useLocales();
  const { configurations } = useTenantConfigData();
  useEffect(() => {
    setfileImg('');
    if (binary?.data) {
      setfileImg(`data:${binary?.contentType};base64,${binary?.data}`);
    }
  }, [patient, binary]);

  const user = useAuth();

  const [userId, setUserId] = useState<string>();
  const [fhirUserId, setFhirUserId] = useState<string>();
  const [patientMrn, setPatientMrn] = useState<string>();
  const [episodeOfCare, setEpisodeOfCare] = useState<string[]>([]);

  const permissions = {
    isAllowedViewEOC: checkAclValidation({
      acls: [crsAcls.CRS.PATIENT.BASIC_INFORMATION.VIEW_EOC],
    }),
    isAllowedViewTags: checkAclValidation({
      acls: [crsAcls.CRS.PATIENT.BASIC_INFORMATION.VIEW_TAGS],
    }),
  };

  const getEpisodeOfCare = useCallback(async () => {
    if (!patient && !permissions?.isAllowedViewEOC) return;

    const episodeOfCarePatient = await findEpisodeOfCare({ patient: patient?.id });
    const mapEOC = getEOCLastByCode(episodeOfCarePatient);
    if (mapEOC && mapEOC?.length > 0) {
      setEpisodeOfCare(mapEOC);
    }
  }, [patient, permissions?.isAllowedViewEOC]);

  useEffect(() => {
    getEpisodeOfCare();
  }, [getEpisodeOfCare]);

  const handlFileRejections = useCallback(
    (fileRejections: FileRejection[]) => {
      if (!patientId) return;
      setFileRejectionsByPatient((currFileRejectionsByPatient) => ({
        ...currFileRejectionsByPatient,
        [patientId]: fileRejections,
      }));
    },
    [patientId]
  );

  useEffect(() => {
    const currentUser = user.getCurrentUser();
    if (currentUser.id === undefined) return;
    setUserId(currentUser.id);
    if (currentUser.user_fhir_uri === undefined) return;
    setFhirUserId(currentUser.user_fhir_uri);
  }, [user]);

  const MiNTIdentifier = patient?.identifier?.find(
    (e) => e.system === configurations?.mint?.programIdentifier
  )
    ? true
    : false;

  const patientDetails =
    patient?.gender &&
    patient?.birthDate &&
    patient?.name?.[0]?.given?.[0] &&
    patient?.name?.[0]?.family &&
    patient?.getAddresses &&
    patient.getAddresses()?.length! > 0
      ? true
      : false;

  useEffect(() => {
    if (!patient) return;
    const tempMrn = patient.getMRN(configurations?.mpi?.systemMrn)?.value;
    setPatientMrn(tempMrn);
  }, [patient, configurations]);

  return (
    <PatientLayout title={i18n('patients.details.title', 'crs')}>
      <StandardDialogProvider>
        <Grid container>
          <Grid item xs={12}>
            <Card
              sx={{
                m: 2,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#F1F4F7',
                border: `1px solid #008ECC`,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Grid item xs>
                  <Stack direction="column" sx={{ m: 1 }}>
                    <UploadAvatar
                      file={fileimg.toString()}
                      patient={patient}
                      sx={{ border: `1px solid #008ECC`, height: 80, width: 80 }}
                      onFileRejections={handlFileRejections}
                      showFileRejections={false}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={9} sx={{ mb: episodeOfCare ? '10px' : 0 }}>
                  {patient && isSuccess && (
                    <PatientBasicInfo
                      patient={patient}
                      episodeOfCare={episodeOfCare}
                      permissions={permissions}
                      onRefresh={() => refreshPatient()}
                    />
                  )}
                </Grid>
                <Grid item xs>
                  <Stack sx={{ m: 3 }} spacing={1.5} alignItems="flex-end">
                    <ProviderWindow
                      userId={userId ?? ''}
                      fhirUserId={fhirUserId ?? ''}
                      patientId={patientId ?? null}
                      patientMiNTIdentifier={MiNTIdentifier}
                      patientData={patientDetails}
                    />
                    {!!patientId && (
                      <IcomxResourceLauncher resourceType="Patient" resourceId={patientId} />
                    )}
                  </Stack>
                </Grid>
              </Box>

              {!!patientId && !!fileRejectionsByPatient?.[patientId]?.length && (
                <Grid item xs={10} sm={8} sx={{ marginX: 2, marginBottom: 1, marginTop: -3 }}>
                  <RejectionFiles
                    variant="Alert"
                    onClose={() => {
                      handlFileRejections([]);
                    }}
                    fileRejections={fileRejectionsByPatient[patientId]}
                  />{' '}
                </Grid>
              )}
            </Card>
            <Card sx={{ mx: 2 }}>
              {patient && isSuccess && (
                <PatientTabs patient={patient} onRefresh={() => refreshPatient()} />
              )}
            </Card>
          </Grid>
        </Grid>
      </StandardDialogProvider>
    </PatientLayout>
  );
}
